/**
 * @module exposed
 * @since 1.7.0
 * @requires exposed/rect
 */

import { rect } from './__rect';

/**
 * Subtract first number from second
 * @private
 * @param  {number} a
 * @param  {number} b
 * @return {number}
 */
const sub = (a, b) => a - b;

/**
 * Subtract second number from first
 * @private
 * @param  {number} a
 * @param  {number} b
 * @return {number}
 */
const revSub = (a, b) => b - a;

/**
 * Determines whether an element is visible inside a given container by a certain percent on both axis X and Y.
 * @param  {DOMElement} element The element to check.
 * @param  {DOMElement|Window} [options.container] The element's container.
 * @param  {Number} [options.percent] The percentage of the element that should be accounted for.
 * @return {Boolean}
 *
 * @example
 * const container = document.querySelector('.my-slideshow-wrapper');
 * const element = container.querySelector('.slide');
 * exposed(
 *   element,
 *   {
 *       container,
 *       percent: 75
 *   }
 * );
 */
export function exposed(element, { container = window, percent = 50 } = {}) {
    const inner = rect(element);
    const outer = rect(container);
    const fraction = percent / 100; // fraction of surface we want exposed

    /**
     * @typedef  SufficiantlyExposedArguments
     * @type     {Array}
     * @property {string}   0 [prop] - Property we're going to measure
     * @property {string}   1 [measurement] - Element's axis
     * @property {function} 2 [calculator] - Function for calculating the diff
     */

    /**
     * Element is sufficiently exposed (on 1 axis)
     * @param  {SufficiantlyExposedArguments} [argument] <a href="#~SufficiantlyExposedArguments">SufficiantlyExposedArguments</a>
     * @return {boolean}
     */
    const sufficiantlyExposed = ([prop, measurement, calculator]) => calculator(inner[prop], outer[prop]) + inner[measurement] >= inner[measurement] * fraction;

    if (['width', 'height'].some((prop) => inner[prop] === 0)) {
        return false;
    }

    // Checks are performed ordered by likelihood of failure (most to least)
    //   considering some checks stop after one success
    return [
        ['top', 'height', sub],
        ['left', 'width', sub],
        ['right', 'width', revSub],
        ['bottom', 'height', revSub]
    ].every(sufficiantlyExposed);
}
